import Link from 'next/link'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { BlogTag } from '@/components/elements/Blog/BlogTag'
import { Interviewee } from '@/components/elements/Blog/Interviewee'
import { Spacer } from '@/components/elements/Spacer'

import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { Blog } from '@/types/blog'

export const PickUpArticle = ({
  _id,
  coverImage,
  title,
  tag,
  interviewee,
  body,
  publishDate,
  pickUpSummary,
}: Blog) => {
  const isMobile = useMediaQuery('(max-width: 1047px)')
  const cover = isMobile ? coverImage.spCoverImage : coverImage.pcCoverImage

  return (
    <Link href={`/news/blog/${_id}`}>
      <a>
        <Article>
          <ImgWrapper>
            <img src={cover.src} alt={cover.altText} />
          </ImgWrapper>
          <Content>
            <Time dateTime={publishDate}>
              {formatDate(publishDate, 'yyyy.MM.dd')}
            </Time>
            <Spacer h={{ sp: 8, pc: 13 }} />
            <Title>{title}</Title>
            <Spacer h={{ sp: 0, pc: 20 }} />
            <Description>{pickUpSummary}</Description>
            <Spacer h={{ sp: 16, pc: 30 }} />
            <TagWrapper>
              {tag.map((item) => (
                <BlogTag {...item} key={item._id} />
              ))}
            </TagWrapper>
            <Spacer h={{ sp: 16, pc: 20 }} />
            <Interviewee {...interviewee} />
          </Content>
        </Article>
      </a>
    </Link>
  )
}

const Article = styled.article`
  display: flex;
  gap: 44px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 0;
    position: relative;
    height: 422px;
  }
`

const ImgWrapper = styled.div`
  margin: 0 auto;

  @media screen and (max-width: 1047px) {
    margin: 0;
  }

  > img {
    width: 520px;
    height: 309px;
    border-radius: 12px;
    object-fit: cover;

    @media screen and (max-width: 1047px) {
      width: 100%;
      height: 232px;
      border-radius: 0;
    }
  }
`

const Content = styled.div`
  @media screen and (max-width: 1047px) {
    position: absolute;
    top: 180px;
    left: 0;
    right: 0;
    height: 243px;
    width: calc(100% - 40px);
    margin: 0 auto;
    padding: 18px 22px 24px;
    border: 2px solid ${theme.colors.backgroundPaleBlue};
    border-radius: 8px;
    background-color: #ffffff;
  }
`

const Time = styled.time`
  font-size: 12px;
  letter-spacing: 1.2px;
  line-height: 25px;
  color: ${theme.colors.textSecond};
`

const Title = styled.h3`
  font-size: 28px;
  font-weight: bold;
  letter-spacing: 2.8px;
  line-height: 38px;
  color: ${theme.colors.textTitle};

  @media screen and (max-width: 1047px) {
    font-size: 16px;
    letter-spacing: 1.6px;
    line-height: 24px;
  }
`

const Description = styled.p`
  font-size: 14px;
  letter-spacing: 0.28px;
  line-height: 24px;
  color: ${theme.colors.textTitle};

  @media screen and (max-width: 1047px) {
    display: none;
  }
`

const TagWrapper = styled.div`
  display: flex;
  align-items: center;
`
